import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
  Alert,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import authService from '../services/authService';
import { invitationService } from '../services/invitationService';
import { saveReturnTo } from '../services/returnTo';
import { extractErrorMessage } from '../utils/errorUtils';

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [alreadyAccepted, setAlreadyAccepted] = useState(false);
  const [wrongAccount, setWrongAccount] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const load = async () => {
      try {
        const info = await invitationService.getInvitation(token);
        if (cancelled) return;

        setCompanyName(info.companyName);

        if (info.expired) {
          setError('Термін дії запрошення закінчився. Попросіть надіслати нове.');
          setLoading(false);
          return;
        }

        if (info.accepted) {
          setAlreadyAccepted(true);
          setLoading(false);
          return;
        }

        const authenticated = await authService.isAuthenticated();
        if (cancelled) return;

        if (!authenticated) {
          setLoading(false);
          return;
        }

        const result = await invitationService.acceptInvitation(token);
        if (cancelled) return;
        navigate(`/companies/${result.companyId}/home`, { replace: true });
      } catch (err) {
        if (cancelled) return;
        // Backend's SecurityException (invitation is for a different email) maps to 403 — the
        // browser has a session, just not the one this invite was sent to.
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          setWrongAccount(true);
        } else {
          setError(extractErrorMessage(err, 'Запрошення не знайдено або більше не дійсне'));
        }
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  const handleLogin = () => {
    if (token) {
      saveReturnTo(`/invite/${token}`);
    }
    authService.loginWithGoogle();
  };

  const handleSwitchAccount = () => {
    if (token) {
      saveReturnTo(`/invite/${token}`);
    }
    authService.logout();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}

          {!error && wrongAccount && (
            <>
              <Alert severity="warning" sx={{ width: '100%', mb: 3 }}>
                Це запрошення призначене для іншої пошти. Ви увійшли під іншим акаунтом Google.
              </Alert>
              <Button variant="contained" onClick={handleSwitchAccount}>
                Вийти й обрати інший акаунт
              </Button>
            </>
          )}

          {!error && !wrongAccount && alreadyAccepted && (
            <>
              <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
                Це запрошення до «{companyName}» вже прийнято
              </Typography>
              <Button variant="contained" onClick={() => navigate('/')}>
                На головну
              </Button>
            </>
          )}

          {!error && !wrongAccount && !alreadyAccepted && (
            <>
              <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
                Вас запросили приєднатися до компанії «{companyName}»
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Увійдіть через Google, щоб прийняти запрошення
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<GoogleIcon />}
                onClick={handleLogin}
              >
                Увійти через Google
              </Button>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
